/**
 * Cinsiyet filtresi — çoklu seçim + kategori/facet URL senkronizasyonu.
 *
 * Mantık:
 * - 0 seçim  → reset URL (collections/all)
 * - 1 seçim  → ilgili koleksiyon URL'si (filtre parametresi olmadan)
 * - 2+ seçim → collections/all + çoklu facet parametreleri
 */
(function () {
  "use strict";

  const STORAGE_PREFIX = "hrcGenderDismissed:";

  function storageKey(handle) {
    return STORAGE_PREFIX + (handle || "_global");
  }

  function isDismissed(handle) {
    try {
      return sessionStorage.getItem(storageKey(handle)) === "1";
    } catch {
      return false;
    }
  }

  function setDismissed(handle, dismissed) {
    try {
      const key = storageKey(handle);
      if (dismissed) sessionStorage.setItem(key, "1");
      else sessionStorage.removeItem(key);
    } catch {
      /* ignore */
    }
  }

  function readConfig(block) {
    const configId = block.dataset.genderConfigId;
    if (!configId) return null;
    const el = document.getElementById(configId);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch {
      return null;
    }
  }

  function normalizeLabel(label) {
    return String(label || "").trim().toLowerCase();
  }

  function buildRouteMap(config, inputs) {
    const map = new Map();
    const entries = [
      [config.valueKadin, config.urlKadin],
      [config.valueErkek, config.urlErkek],
    ];
    entries.forEach(([label, url]) => {
      if (!label || !url) return;
      map.set(normalizeLabel(label), url);
    });

    // Prefer live data-collection-url from rendered checkboxes
    Array.from(inputs || []).forEach((input) => {
      const label = input.dataset.genderLabel;
      const url = input.dataset.collectionUrl;
      if (label && url) map.set(normalizeLabel(label), url);
    });

    return map;
  }

  function getSelectedGenders(inputs) {
    return Array.from(inputs)
      .filter((input) => input.checked)
      .map((input) => ({
        label: input.dataset.genderLabel || "",
        value: input.value,
        collectionUrl: input.dataset.collectionUrl || "",
      }));
  }

  function isGenderParam(key, paramName) {
    if (!key) return false;
    if (key === paramName) return true;
    return key.includes("target-gender");
  }

  function preserveNonGenderParams(targetUrl, currentUrl, paramName) {
    const target = new URL(targetUrl, window.location.origin);
    const current = new URL(currentUrl, window.location.origin);

    current.searchParams.forEach((value, key) => {
      if (isGenderParam(key, paramName)) return;
      if (key === "sort_by") {
        target.searchParams.set("sort_by", value);
        return;
      }
      if (key.startsWith("filter.")) {
        target.searchParams.append(key, value);
      }
    });

    return target;
  }

  function buildNavigationUrl(selected, config, inputs) {
    const { paramName, allUrl, resetUrl } = config;
    const routeMap = buildRouteMap(config, inputs);

    if (!selected.length) {
      const base = allUrl || resetUrl;
      if (!base) return resetUrl || allUrl;
      return preserveNonGenderParams(base, window.location.href, paramName).toString();
    }

    if (selected.length === 1) {
      const one = selected[0];
      const collectionUrl =
        one.collectionUrl || routeMap.get(normalizeLabel(one.label));
      if (collectionUrl) {
        return preserveNonGenderParams(
          collectionUrl,
          window.location.href,
          paramName,
        ).toString();
      }
    }

    // Multi-select: stay on all products; append facet params when available
    const url = preserveNonGenderParams(
      allUrl || resetUrl,
      window.location.href,
      paramName,
    );
    selected.forEach(({ value }) => {
      if (value) url.searchParams.append(paramName, value);
    });
    return url.toString();
  }

  function saveFacetScroll() {
    if (window.hrcFacetScroll && typeof window.hrcFacetScroll.save === "function") {
      window.hrcFacetScroll.save();
      return;
    }
    try {
      const y = window.scrollY || window.pageYOffset || 0;
      sessionStorage.setItem("hrcFacetScrollY", String(Math.round(y)));
    } catch {
      /* ignore */
    }
  }

  function queueFacetNavigation(navigateFn) {
    if (window.hrcFacetLoading && typeof window.hrcFacetLoading.queue === "function") {
      window.hrcFacetLoading.queue(navigateFn);
      return;
    }
    saveFacetScroll();
    navigateFn();
  }

  function buildGenderResetUrl(config) {
    const paramName = config.paramName;
    // Always leave gender collections (erkek/kadin/…) — url_to_remove can keep you there
    const base = config.allUrl || config.resetUrl;
    if (!base) return null;
    return preserveNonGenderParams(base, window.location.href, paramName).toString();
  }

  function dismissGenderCollections(config, impliedCollection, inputs) {
    if (impliedCollection) setDismissed(impliedCollection, true);

    const routeMap = buildRouteMap(config, inputs);
    routeMap.forEach(function (url) {
      try {
        const path = new URL(url, window.location.origin).pathname;
        const parts = path.split("/").filter(Boolean);
        const handle = parts[parts.length - 1];
        if (handle) setDismissed(handle, true);
      } catch {
        /* ignore */
      }
    });
  }

  function resetGenderFilter(block, config) {
    const inputs = block.querySelectorAll("[data-hrc-gender-input]");
    dismissGenderCollections(config, block.dataset.impliedCollection || "", inputs);

    inputs.forEach(function (input) {
      input.checked = false;
      input.removeAttribute("data-implied-active");
    });

    const target = buildGenderResetUrl(config);
    queueFacetNavigation(function () {
      if (target && target !== window.location.href) {
        window.location.href = target;
      } else if (target) {
        window.location.reload();
      } else if (window.hrcFacetLoading && typeof window.hrcFacetLoading.hide === "function") {
        window.hrcFacetLoading.hide();
      }
    });
  }

  function navigateForSelection(inputs, config) {
    const selected = getSelectedGenders(inputs);
    const target = buildNavigationUrl(selected, config, inputs);
    queueFacetNavigation(function () {
      if (target && target !== window.location.href) {
        window.location.href = target;
      } else if (target) {
        window.location.reload();
      } else if (window.hrcFacetLoading && typeof window.hrcFacetLoading.hide === "function") {
        window.hrcFacetLoading.hide();
      }
    });
  }

  function bindGenderFilterBlock(block) {
    if (!block || block.dataset.hrcGenderBound === "true") return;
    block.dataset.hrcGenderBound = "true";

    const config = readConfig(block);
    if (!config) return;

    const impliedGender = block.dataset.impliedGender || "";
    const impliedCollection = block.dataset.impliedCollection || "";
    const urlActive = block.dataset.genderUrlActive === "true";
    const inputs = block.querySelectorAll("[data-hrc-gender-input]");

    if (impliedGender && !urlActive && isDismissed(impliedCollection)) {
      inputs.forEach((input) => {
        if (input.dataset.impliedActive === "true") {
          input.checked = false;
          input.removeAttribute("data-implied-active");
        }
      });
    }

    block.querySelectorAll("[data-hrc-gender-reset]").forEach((resetLink) => {
      if (resetLink.dataset.hrcGenderResetBound === "true") return;
      resetLink.dataset.hrcGenderResetBound = "true";
      resetLink.addEventListener("click", function (event) {
        event.preventDefault();
        event.stopPropagation();
        resetGenderFilter(block, config);
      });
    });

    inputs.forEach((input) => {
      input.addEventListener("change", (event) => {
        event.stopPropagation();

        const isImplied = input.dataset.impliedActive === "true";

        if (!input.checked) {
          if (
            isImplied ||
            (impliedGender &&
              normalizeLabel(input.dataset.genderLabel) ===
                normalizeLabel(impliedGender))
          ) {
            if (impliedCollection) setDismissed(impliedCollection, true);
          }
          input.removeAttribute("data-implied-active");
        } else if (impliedCollection) {
          setDismissed(impliedCollection, false);
        }

        if (document.body.classList.contains("hrc-filters-drawer-open")) return;
        navigateForSelection(inputs, config);
      });
    });
  }

  window.hrcGenderFilter = {
    applyFromForm: function (form) {
      if (!form) return null;
      const block = form.querySelector("[data-hrc-gender-filter]");
      if (!block) return null;
      const config = readConfig(block);
      if (!config) return null;
      const inputs = block.querySelectorAll("[data-hrc-gender-input]");
      return buildNavigationUrl(getSelectedGenders(inputs), config, inputs);
    },
    paramNameFromForm: function (form) {
      if (!form) return "";
      const block = form.querySelector("[data-hrc-gender-filter]");
      if (!block) return "";
      const config = readConfig(block);
      return (config && config.paramName) || "";
    },
  };

  function init(root) {
    const scope = root || document;
    scope.querySelectorAll("[data-hrc-gender-filter]").forEach(bindGenderFilterBlock);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => init(document));
  } else {
    init(document);
  }

  document.addEventListener("shopify:section:load", (event) => {
    init(event.target);
  });
})();
